/*
Description: Reports the on-screen keyboard height on Android, 0 otherwise.

At targetSdk 36 Android no longer resizes the window for the keyboard: edge-to-edge
means the app consumes IME insets itself, so adjustResize does not apply. RN's
KeyboardAvoidingView cannot cover for it either — "height" restores a stale frame
once the keyboard hides, and "padding" moves the box that absolutely positioned
children resolve bottom: 0 against. Both leave a gap under the content.

Callers offset with marginBottom instead: it shrinks the box while the keyboard is
up and returns to exactly 0 when it hides.

The returned value adds insets.bottom while the keyboard is up. endCoordinates
reports only the keyboard panel, not the navigation bar it draws over, so the raw
height leaves that strip uncovered and clips whatever sits lowest.
*/

import { useState, useEffect } from 'react';
import { Keyboard } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { isAndroid } from '@global/screens';

const useAndroidKeyboardHeight = () => {
  const insets = useSafeAreaInsets();
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  useEffect(() => {
    if (!isAndroid) {
      return undefined;
    }
    // RN reports ime.bottom - systemBars.bottom, which goes negative when the IME
    // is "shown" with no panel (hardware keyboard, floating toolbar) on Android <= 15,
    // where the app opts out of edge-to-edge. Treat that as no keyboard.
    const onShow = Keyboard.addListener('keyboardDidShow', (e) =>
      setKeyboardHeight(Math.max(e?.endCoordinates?.height || 0, 0)),
    );
    const onHide = Keyboard.addListener('keyboardDidHide', () =>
      setKeyboardHeight(0),
    );
    return () => {
      onShow.remove();
      onHide.remove();
    };
  }, []);

  return keyboardHeight ? keyboardHeight + insets.bottom : 0;
};

export default useAndroidKeyboardHeight;
